import React, { useMemo } from 'react';

import Tooltip from '@components/tooltip/tooltip';
import { Label, Select, TextInput } from '@trussworks/react-uswds';

import { SelectInputOption } from '@api/types';
import { formatReadOnlyValue } from '@utils/helpers';
import classnames from 'classnames';

import './selectInput.scss';

interface SelectInputProps {
  label: string;
  name: string;
  onChange: ({
    e,
    value,
    onChangeProps,
  }: {
    e: React.ChangeEvent<HTMLSelectElement>;
    value: string | number;
    onChangeProps?: Record<string, unknown>;
  }) => void;
  options: SelectInputOption[];

  className?: string;
  defaultValue?: string | number;
  defaultOptionText?: string;
  displayFullWidth?: boolean;
  hasError?: boolean;
  isReadOnly?: boolean;
  isRequired?: boolean;
  onBlur?: (e: React.FocusEvent<HTMLSelectElement>) => void;
  onChangeProps?: Record<string, unknown>;
  showOptionalText?: boolean;
  tooltipContent?: React.ReactNode;
  value?: string | number;
}

const SelectInput: React.FC<SelectInputProps> = ({
  className,
  defaultOptionText = '-Select Option-',
  defaultValue,
  displayFullWidth = true,
  hasError,
  isReadOnly,
  isRequired,
  label,
  name,
  onBlur = () => {},
  onChange,
  onChangeProps = {},
  options,
  showOptionalText = true,
  tooltipContent,
  value,
}) => {
  const containerStyles = classnames('select-container', className, {
    'width-full': displayFullWidth,
  });
  const labelStyles = classnames({
    required: isRequired,
    optional: !isRequired && showOptionalText,
  });
  const defaultOptionStyles: React.CSSProperties = {
    display: isRequired && showOptionalText ? 'none' : 'inline',
  };

  const inputValue = value ?? defaultValue;
  const hasSelectedOption = useMemo(
    () => (!inputValue ? true : options.some((option) => option.value === inputValue)),
    [options, inputValue]
  );

  const readOnlyValue = useMemo(() => {
    const formattedValue = formatReadOnlyValue(value ?? defaultValue);

    if (options.length > 0) {
      const foundOption = options.find((option) => {
        return option.value === formattedValue;
      });

      if (foundOption) {
        return foundOption.label;
      }
    }

    if (formattedValue) {
      return formattedValue;
    }

    return value;
  }, [defaultValue, options, value]);

  return (
    <>
      {typeof label === 'string' && (
        <Label className={labelStyles} htmlFor={name}>
          <span id={`${name}_label`}>{label}</span>
          {tooltipContent && <Tooltip content={tooltipContent} header={label} name={name} />}
        </Label>
      )}
      {isReadOnly ? (
        <div>
          <TextInput
            className='margin-bottom-2'
            id={name}
            name={name}
            readOnly={true}
            type='text'
            value={readOnlyValue}
          />
        </div>
      ) : (
        <div className={containerStyles}>
          <Select
            aria-invalid={hasError ? 'true' : 'false'}
            aria-required={isRequired ? 'true' : 'false'}
            defaultValue={defaultValue}
            disabled={isReadOnly}
            id={name}
            name={name}
            onBlur={onBlur}
            onChange={(e) => onChange({ e, value: e.target.value, onChangeProps })}
            required={isRequired}
            value={value}
          >
            <option value='' style={defaultOptionStyles}>
              {hasSelectedOption ? defaultOptionText : value || defaultValue}
            </option>
            {options.map((option) => {
              const key = typeof option === 'string' ? option : option.value;

              return (
                <option key={key} value={option.value}>
                  {option.label}
                </option>
              );
            })}
          </Select>
        </div>
      )}
    </>
  );
};

export default SelectInput;
